// types/adhesion.types.ts - Version mise à jour avec adhésions multi-niveaux

// ==========================================
// TYPES POUR ADHÉSIONS MULTI-NIVEAUX
// ==========================================

export type MembershipLevel = 'sympathisant' | 'partial_member' | 'full_member';
export type NipStatus = 'has_nip' | 'no_nip_wont_get' | 'no_nip_will_get' | 'unknown';

// Interface pour les permissions selon le niveau
export interface MembershipPermissions {
    canViewEvents: boolean;
    canAttendEvents: boolean;
    canAccessMemberDirectory: boolean;
    canReceiveMemberCard: boolean;
    canVoteInternalElections: boolean;
    canReferMembers: boolean;
    canAccessPrivateContent: boolean;
    canDownloadOfficialDocuments: boolean;
}

// Matrice des permissions par niveau
export const PERMISSION_MATRIX: Record<MembershipLevel, MembershipPermissions> = {
    sympathisant: {
        canViewEvents: true,
        canAttendEvents: true,
        canAccessMemberDirectory: false,
        canReceiveMemberCard: false,
        canVoteInternalElections: false,
        canReferMembers: true,
        canAccessPrivateContent: false,
        canDownloadOfficialDocuments: false
    },
    partial_member: {
        canViewEvents: true,
        canAttendEvents: true,
        canAccessMemberDirectory: true,
        canReceiveMemberCard: false, // Pas de carte sans NIP
        canVoteInternalElections: false,
        canReferMembers: true,
        canAccessPrivateContent: true,
        canDownloadOfficialDocuments: true
    },
    full_member: {
        canViewEvents: true,
        canAttendEvents: true,
        canAccessMemberDirectory: true,
        canReceiveMemberCard: true,
        canVoteInternalElections: true,
        canReferMembers: true,
        canAccessPrivateContent: true,
        canDownloadOfficialDocuments: true
    }
};

// Interface étendue pour les données de formulaire
export interface AdhesionFormData {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    nip: string;
    dateOfBirth: string;
    placeOfBirth: string;
    country: string;
    province: string;
    department: string;
    city: string;
    customCity: string;
    profession: string; // ✅ DEVIENT OBLIGATOIRE
    gender: string; // ✅ NOUVEAU CHAMP (Homme/Femme)
    referredBy: string;
}

export interface MemberData {
    id?: string;
    member_number: string;
    first_name: string;
    last_name: string;
    email?: string;
    phone: string;
    nip?: string;
    city: string;
    province: string;
    country?: string;
    date_of_birth?: string;
    place_of_birth?: string;
    profession?: string; // Dans MemberData, peut rester optionnel pour compatibilité
    gender?: string; // ✅ NOUVEAU CHAMP
    referral_code: string;
    created_at: string;
    badges?: string[];
    selfie_url?: string;
    signature_data?: string;
    department?: string; // ✅ AJOUT pour le PDF

    // ✅ AJOUTS pour les nouvelles fonctionnalités
    identity_document_url?: string;
    referred_by?: string;
    referrals_count?: number;
    status?: string;
    updated_at?: string;

    // ✅ NOUVEAUX CHAMPS MULTI-NIVEAUX
    membership_level?: MembershipLevel;
    nip_status?: NipStatus;
    can_upgrade?: boolean;
    paper_reference?: string; // Référence vers others_members
}

export interface ReferrerInfo {
    firstName: string;
    lastName: string;
    city: string;
    province: string;
    email?: string; // ✅ AJOUT pour les notifications email
}

// ==========================================
// NOUVEAUX TYPES POUR LES FONCTIONNALITÉS AVANCÉES
// ==========================================

// Interface pour la création de membre (useMembership)
export interface MembershipFormData {
    firstName: string;
    lastName: string;
    phone: string;
    email?: string | undefined;
    nip?: string | undefined;
    country: string;
    province: string;
    department?: string | undefined;
    city: string;
    customCity?: string | undefined;
    profession: string;
    gender: string;
    referredBy?: string | undefined;
    selfieBase64?: string | null | undefined;
    identityDocumentBase64?: string | null | undefined;
    signatureData?: string | null | undefined;
    dateOfBirth?: string | undefined;
    placeOfBirth?: string | undefined;
}

// Interface pour le membre créé (retour API)
export interface CreatedMember {
    id: string;
    member_number: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    nip: string | null;
    country: string;
    province: string;
    department: string | null;
    city: string;
    profession: string;
    gender: string;
    selfie_url?: string;
    identity_document_url?: string;
    signature_data?: string | null;
    referral_code: string;
    badges: string[];
    referrals_count: number;
    created_at: string;
}

// Types pour les emails
export interface EmailStatus {
    welcome?: { success: boolean; message: string };
    confirmation?: { success: boolean; message: string };
    referralNotification?: { success: boolean; message: string };
}

// Types pour le partage social
export interface ShareLinks {
    referralLink: string;
    facebookMessage: string;
    whatsappMessage: string;
    whatsapp: string;
    facebook: string;
    telegram: string;
    twitter: string;
    linkedin: string;
}

export type SocialPlatform = 'whatsapp' | 'facebook' | 'telegram' | 'twitter' | 'linkedin';

// Types pour les badges
export type BadgeType =
    | 'Pionnier'
    | 'Recruteur'
    | 'Bâtisseur Or'
    | 'Ambassadeur'
    | 'Légende';

// Types pour les statistiques
export interface GlobalStats {
    total_members: number;
    total_referrals: number;
    assembly_attendees: number;
    updated_at: string;
}

// Types pour la validation
export interface ValidationResult {
    valid: boolean;
    message?: string;
    referrer?: ReferrerInfo;
}

// Types pour l'authentification
export interface AuthCredentials {
    firstName?: string;
    lastName: string;
    memberNumber?: string;
    nip?: string;
    phone?: string;
    email?: string;
}

export interface AuthResult {
    success: boolean;
    member?: MemberData;
    error?: string;
}

// ==========================================
// CONSTANTES UTILES
// ==========================================

export const GABON_PROVINCES = [
    'Estuaire',
    'Haut-Ogooué',
    'Moyen-Ogooué',
    'Ngounié',
    'Nyanga',
    'Ogooué-Ivindo',
    'Ogooué-Lolo',
    'Ogooué-Maritime',
    'Woleu-Ntem'
] as const;

export type GabonProvince = typeof GABON_PROVINCES[number];

export const VALIDATION_RULES = {
    NIP_PATTERN: /^[A-Za-z0-9]{2}-[A-Za-z0-9]{4}-[A-Za-z0-9]{8}$/,
    EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    PHONE_PATTERN: /^(\+241|241)?[0-9]{8}$/,
    MEMBER_NUMBER_PATTERN: /^UDB[0-9]{6}$/,
    REFERRAL_CODE_PATTERN: /^[A-Z0-9]{6}$/
} as const;
